import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ChatMessage, ChatMessageResponse, ChatMessagesResponse } from '@twaice-fe/shared/models';
import { keysToCamel } from '@twaice-fe/shared/utilities';
import mixpanel from 'mixpanel-browser';
import { Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  /** Emits after a note is created, updated, or deleted so views (e.g. the notes widget) can refresh. */
  readonly notesChanged$: Observable<void>;
  private readonly notesChangedSubject = new Subject<void>();

  constructor(private http: HttpClient) {
    this.notesChanged$ = this.notesChangedSubject.asObservable();
  }

  fetchChatMessages(customerBk: string, systemBk: string): Observable<ChatMessage[]> {
    const url = `analytics/chat/customers/${customerBk}/systems/${systemBk}/messages`;

    return this.http
      .get<{ data: ChatMessagesResponse }>(url)
      .pipe(
        map((res: { data: ChatMessagesResponse }) =>
          res.data.items.map((item: ChatMessageResponse) => keysToCamel(item) as ChatMessage)
        )
      );
  }

  postChatMessage(
    customerBk: string,
    systemBk: string,
    message: string,
    tags: string[],
    includeLink: boolean,
    parent?: string,
    relatedEntities?: string[],
    timestamp?: Date
  ): Observable<ChatMessage> {
    const url = `analytics/chat/customers/${customerBk}/systems/${systemBk}/messages`;
    return this.http
      .post<{ data: ChatMessageResponse }>(url, {
        message: message,
        tags: tags,
        source_url: includeLink ? `${location.pathname}${location.search ?? ''}` : undefined,
        parent: parent ?? undefined,
        related_entities: relatedEntities ?? undefined,
        timestamp: timestamp ? timestamp.toISOString() : undefined,
      })
      .pipe(
        map((res: { data: ChatMessageResponse }) => keysToCamel(res.data)),
        tap(() => {
          mixpanel.track('Note Added', { tags, sourceUrl: includeLink ? location.href : undefined });
          this.notesChangedSubject.next();
        })
      );
  }

  patchChatMessage(
    customerBk: string,
    systemBk: string,
    timestamp: string,
    message?: string,
    tags?: string[]
  ): Observable<ChatMessage> {
    const url = `analytics/chat/customers/${customerBk}/systems/${systemBk}/messages/${timestamp}`;
    const body = {
      ...(message ? { message: message } : {}),
      ...(tags ? { tags: tags } : {}),
    };

    return this.http.patch<{ data: ChatMessageResponse }>(url, body).pipe(
      map((res: { data: ChatMessageResponse }) => keysToCamel(res.data)),
      tap(() => {
        mixpanel.track('Note Updated', { tags });
        this.notesChangedSubject.next();
      })
    );
  }

  deleteChatMessage(customerBk: string, systemBk: string, timestamp: string): Observable<object> {
    const url = `analytics/chat/customers/${customerBk}/systems/${systemBk}/messages/${timestamp}`;
    return this.http.delete(url).pipe(tap(() => this.notesChangedSubject.next()));
  }
}
